// src/pages/admin/AdminActivityLogs.js
import React, { useState, useEffect, useCallback } from 'react';
import { logService } from '../../services/apiServices';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showErrorToast } from '../../utils/toastUtils';
import {
  Search, Download, RefreshCw, Activity, LogIn, AlertTriangle,
  ChevronUp, ChevronDown, CheckCircle2, Shield, Clock, Server, User as UserIcon
} from 'lucide-react';

const AdminActivityLogs = () => {
  const { user: currentUser } = useAuth();

  // State for logs
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ totalLogs: 0, todayLogs: 0, loginLogs: 0, errorLogs: 0 });

  // UI States
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Server-side filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    actionType: '',
    startDate: '',
    endDate: ''
  });

  // Debounce search
  const [searchInput, setSearchInput] = useState('');

  const [pagination, setPagination] = useState({
    page: 1, limit: 20, total: 0, pages: 1
  });

  const actionTypes = [
    { value: '', label: 'Toutes les actions' },
    { value: 'USER_LOGIN', label: 'Connexion' },
    { value: 'USER_LOGOUT', label: 'Déconnexion' },
    { value: 'USER_CREATE', label: 'Création compte' },
    { value: 'USER_UPDATE', label: 'Modif. compte' },
    { value: 'USER_DELETE', label: 'Suppr. compte' },
    { value: 'ANALYSIS_CREATE', label: 'Nouvelle analyse' },
    { value: 'ANALYSIS_UPDATE', label: 'Modif. analyse' },
    { value: 'ANALYSIS_DELETE', label: 'Suppr. analyse' }
  ];

  // Fetch stats logic
  const fetchStats = async () => {
    try {
      const response = await logService.getActivityStats();
      if (response && response.data) {
        const { totalLogs, actionTypeCounts } = response.data;
        const logins = actionTypeCounts.find(a => a.type === 'USER_LOGIN')?.count || 0;
        const errors = actionTypeCounts.find(a => a.type && a.type.includes('ERROR'))?.count || 0;

        // Very basic "today" logic since backend aggregates by day might vary, 
        // we'll rely on the recently fetched active daily data or just show what we have.
        const todayStr = new Date().toISOString().split('T')[0];
        const todayData = response.data.activityByDay?.find(day => day._id === todayStr);

        setStats({
          totalLogs: totalLogs || 0,
          todayLogs: todayData?.count || 0,
          loginLogs: logins,
          errorLogs: errors
        });
      }
    } catch (error) {
      console.warn('Failed to load activity stats', error);
    }
  };

  // Main fetch function
  const fetchLogs = useCallback(async (page = 1, fetchBackground = false) => {
    if (!currentUser?.companyId) {
      showErrorToast('Impossible de charger les logs : Aucune entreprise associée.');
      setIsLoading(false);
      return;
    }

    if (!fetchBackground) setIsLoading(true);
    setIsRefreshing(true);

    try {
      // Build API params matching backend activityLogController expectations
      const params = {
        page,
        limit: pagination.limit,
        companyId: currentUser.companyId,
        sort: '-timestamp'
      };

      if (filters.search) params.search = filters.search;
      if (filters.actionType) params.actionType = filters.actionType;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await logService.getLogs(params);

      if (response.success) {
        setLogs(response.data || []);
        if (response.pagination) {
          setPagination(prev => ({ ...prev, ...response.pagination }));
        }
      }
    } catch (error) {
      showErrorToast("Erreur lors du chargement des journaux d'activité.");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [currentUser, pagination.limit, filters]);

  // Initial load
  useEffect(() => {
    fetchLogs(1);
    fetchStats();
  }, []);

  // Handle Search Debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        // Change of filters will trigger useEffect below
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Re-fetch when filters change
  useEffect(() => {
    // Only fetch if we're not already loading the initial state
    if (!isLoading) {
      fetchLogs(1, true); // fetch background to avoid spinner flash
    }
  }, [filters.search, filters.actionType, filters.startDate, filters.endDate]);

  const handleExportTxt = () => {
    if (logs.length === 0) return showErrorToast("Aucun log à exporter");

    let content = "===== EXPORT DES JOURNAUX D'ACTIVITÉ (ADMIN) =====\n";
    content += `Généré le: ${new Date().toLocaleString('fr-FR')}\n`;
    content += `Filtres: Recherche: ${filters.search || 'Aucune'} | Action: ${filters.actionType || 'Toutes'}\n`;
    content += `Période: ${filters.startDate || '-'} à ${filters.endDate || '-'}\n`;
    content += "=================================================\n\n";

    logs.forEach(log => {
      const details = typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details || '-');
      const date = new Date(log.timestamp).toLocaleString('fr-FR');
      content += `[${date}] Utilisateur: ${log.userName} | Action: ${log.actionType} | IP: ${log.ipAddress} | Ressource: ${log.resourceType}\n`;
      if (details !== '{}' && details !== '-') {
        content += `    Détails: ${details}\n`;
      }
      content += '\n';
    });

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Ebiosens_Audit_Logs_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getBadgeStyle = (action) => {
    if (!action) return 'bg-gray-100 text-gray-700 border-gray-200';
    if (action.includes('CREATE') || action.includes('APPROVE')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (action.includes('UPDATE')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (action.includes('DELETE') || action.includes('REJECT') || action.includes('ERROR')) return 'bg-rose-50 text-rose-700 border-rose-200';
    if (action.includes('LOGIN') || action.includes('LOGOUT')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getActionIcon = (action) => {
    if (!action) return <Activity size={14} />;
    if (action.includes('CREATE')) return <CheckCircle2 size={14} className="text-emerald-500" />;
    if (action.includes('DELETE')) return <AlertTriangle size={14} className="text-rose-500" />;
    if (action.includes('LOGIN')) return <LogIn size={14} className="text-indigo-500" />;
    if (action.includes('ERROR')) return <AlertTriangle size={14} className="text-rose-600" />;
    return <Activity size={14} className="text-blue-500" />;
  };

  return (
    <div className="p-4 md:p-8 max-w-full mx-auto bg-gray-50/50 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="w-6 h-6 text-blue-600" />
            Journaux d'Audit & Sécurité
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Traçabilité intégrale de l'activité du système — Mode Lecture Seule
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExportTxt}
            disabled={logs.length === 0}
            className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-50 hover:text-blue-600 focus:ring-4 focus:ring-gray-100 transition-all font-medium text-sm shadow-sm disabled:opacity-50"
          >
            <Download size={18} />
            Exporter (TXT)
          </button>
          <button
            onClick={() => fetchLogs(1)}
            disabled={isRefreshing}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-100 transition-all font-medium text-sm shadow-sm"
          >
            <RefreshCw size={18} className={isRefreshing && !isLoading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      {/* KPI Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Volume Total (Global)', value: stats.totalLogs, icon: DatabaseIcon, color: 'blue' },
          { label: 'Activité du Jour', value: stats.todayLogs, icon: ActivityIcon, color: 'emerald' },
          { label: 'Connexions', value: stats.loginLogs, icon: LogInIcon, color: 'blue' },
          { label: 'Avertissements / Erreurs', value: stats.errorLogs, icon: AlertIcon, color: 'rose' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-${stat.color}-50 text-${stat.color}-600`}>
              <stat.icon />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value.toLocaleString('fr-FR')}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Modern Filter Panel */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {/* Search */}
          <div className="md:col-span-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Recherche globale</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Utilisateur, IP, ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
              />
            </div>
          </div>

          {/* Action Filter */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Type d'action</label>
            <select
              value={filters.actionType}
              onChange={(e) => setFilters(p => ({ ...p, actionType: e.target.value }))}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors appearance-none cursor-pointer"
            >
              {actionTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Depuis le</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(p => ({ ...p, startDate: e.target.value }))}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Jusqu'au</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(p => ({ ...p, endDate: e.target.value }))}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Main Table Area */}
      {isLoading ? (
        <div className="flex justify-center items-center py-20 bg-white rounded-xl shadow-sm border border-slate-200">
          <LoadingSpinner />
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-4 bg-gray-50 rounded-full mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Aucun résultat</h3>
          <p className="text-gray-500 mt-1 max-w-sm text-center text-sm">
            Aucun journal d'activité ne correspond à vos filtres. Essayez d'élargir votre recherche.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-48">Horodatage</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-64">Utilisateur</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">Événement</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-40">Accès Réseau</th>
                  <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-16 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => {
                  const isExpanded = expandedRow === log.id;

                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`hover:bg-blue-50/50 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50/30' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : log.id)}
                      >
                        <td className="py-3 px-6 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                            <Clock size={14} className="text-gray-400" />
                            {new Date(log.timestamp).toLocaleString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td className="py-3 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-slate-600">
                                {log.userName ? log.userName.substring(0, 2).toUpperCase() : 'SY'}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-gray-900 block truncate max-w-[180px]">
                                {log.userName || 'Action Système'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-6">
                          <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide border rounded-md flex items-center gap-1.5 w-max ${getBadgeStyle(log.actionType)}`}>
                              {getActionIcon(log.actionType)}
                              {log.actionType || 'INCONNU'}
                            </span>
                            {log.resourceType && (
                              <span className="text-xs font-medium text-gray-500">
                                {log.resourceType}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-6 text-sm text-gray-500 font-mono">
                          {log.ipAddress && log.ipAddress !== '::1' && log.ipAddress !== '127.0.0.1' ? log.ipAddress : 'Local / Interne'}
                        </td>
                        <td className="py-3 px-6 text-center">
                          <button className="p-1 rounded bg-gray-50 hover:bg-gray-200 text-gray-500 transition-colors">
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Details Row */}
                      {isExpanded && (
                        <tr className="bg-slate-50 border-b border-gray-100 shadow-inner">
                          <td colSpan="5" className="p-0">
                            <div className="px-8 py-5 flex gap-6">
                              <div className="flex-1 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                                <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                                  <Server size={14} /> Payload / Métadonnées Systèmes
                                </h4>
                                <pre className="text-[11px] font-mono whitespace-pre-wrap text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-100 max-h-48 overflow-y-auto">
                                  {log.details && Object.keys(log.details).length > 0
                                    ? JSON.stringify(log.details, null, 2)
                                    : 'Aucune donnée associée à cette empreinte.'}
                                </pre>
                              </div>
                              <div className="w-72 bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-3">
                                <h4 className="text-xs font-bold text-gray-500 uppercase mb-1 flex items-center gap-2">
                                  <Shield size={14} /> Tracabilité
                                </h4>
                                <div>
                                  <span className="text-[10px] text-gray-400 block uppercase">ID Trace</span>
                                  <span className="text-xs font-mono text-gray-800 break-all">{log.id}</span>
                                </div>
                                {log.userId && (
                                  <div>
                                    <span className="text-[10px] text-gray-400 block uppercase">User ID</span>
                                    <span className="text-xs font-mono text-gray-800 break-all">{log.userId}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {pagination.pages > 0 && (
            <div className="px-6 py-4 bg-white border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">
                Affiche {(pagination.page - 1) * pagination.limit + 1} à {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total} logs
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:bg-gray-50 transition-colors"
                >
                  Précédent
                </button>
                <div className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-bold border border-blue-100">
                  {pagination.page} / {pagination.pages}
                </div>
                <button
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:bg-gray-50 transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Mini components for stats icons
const DatabaseIcon = () => <Server size={24} />;
const ActivityIcon = () => <Activity size={24} />;
const LogInIcon = () => <LogIn size={24} />;
const AlertIcon = () => <AlertTriangle size={24} />;

export default AdminActivityLogs;